import React from 'react';
import { formatCurrency, getCurrencySymbol } from '../../utils/formatters';
import { History, Pencil, Trash2 } from 'lucide-react';
import ActionMenu from '../common/ActionMenu';
import { useApp } from '../../contexts/AppContext';

const ClientsList = ({
  filteredClients,
  searchTermClients,
  setSearchTermClients,
  clientTypeFilter,
  setClientTypeFilter,
  clientCaFilter,
  setClientCaFilter,
  currentPage,
  setCurrentPage,
  showClientForm,
  setShowClientForm,
  editingClient,
  setEditingClient,
  expandedRows,
  toggleRowExpansion,
  handleClientSubmit,
  deleteClient,
  openClientDetail,
  getClientCA,
  getDisplayClientType,
  Pagination
}) => {
  const { clients, settings, hasWriteAccess, itemsPerPage, setItemsPerPage, getPaginatedItems } = useApp();

  const resetFilters = () => {
    setSearchTermClients('');
    setClientTypeFilter('');
    setClientCaFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="clients" data-testid="clients-section">
      <div className="section-header">
        <h2>Gestion des Clients</h2>
        {hasWriteAccess() && (
          <button 
            onClick={() => setShowClientForm(true)}
            className="btn btn-primary"
            data-testid="btn-new-client"
          >
            Nouveau Client
          </button>
        )}
      </div>

      {/* Filtres clients */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group search-group">
            <label>🔍 Rechercher</label>
            <input 
              type="text"
              placeholder="Nom, SIRET, email, téléphone, adresse..."
              value={searchTermClients}
              onChange={(e) => setSearchTermClients(e.target.value)}
              className="search-input"
              data-testid="search-clients"
            />
          </div>
          <div className="filter-group">
            <label>Filtrer par type</label>
            <select 
              value={clientTypeFilter} 
              onChange={(e) => setClientTypeFilter(e.target.value)}
              className="filter-select"
              data-testid="filter-type-client"
            >
              <option value="">Tous les types</option>
              <option value="GMS">GMS</option>
              <option value="boutique">Boutique</option>
              <option value="restaurant">Restaurant</option>
            </select>
          </div>
          <div className="filter-group">
            <label>CA minimum ({getCurrencySymbol(settings?.devise)})</label>
            <input 
              type="number" 
              value={clientCaFilter} 
              onChange={(e) => setClientCaFilter(e.target.value)}
              placeholder="0"
              min="0"
              step="0.01"
              className="filter-input"
              data-testid="filter-ca-client"
            />
          </div>
          <div className="filter-group">
            <label>&nbsp;</label>
            <button 
              onClick={resetFilters}
              className="btn btn-secondary btn-sm"
              data-testid="btn-reset-filters"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Modal formulaire client */}
      {showClientForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingClient ? 'Modifier le client' : 'Nouveau client'}</h3>
            <form onSubmit={handleClientSubmit} data-testid="client-form">
              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input 
                  type="text" 
                  name="nom_entreprise" 
                  required 
                  defaultValue={editingClient?.nom_entreprise || ''}
                />
              </div>
              <div className="form-group">
                <label>SIRET *</label>
                <input 
                  type="text" 
                  name="siret" 
                  required 
                  defaultValue={editingClient?.siret || ''}
                />
              </div>
              <div className="form-group">
                <label>Adresse *</label>
                <textarea 
                  name="adresse" 
                  required 
                  defaultValue={editingClient?.adresse || ''}
                />
              </div>
              <div className="form-group">
                <label>Téléphone *</label>
                <input 
                  type="tel" 
                  name="telephone" 
                  required 
                  defaultValue={editingClient?.telephone || ''}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  required 
                  defaultValue={editingClient?.email || ''}
                />
              </div>
              <div className="form-group">
                <label>Type de client *</label>
                <select 
                  name="type_client" 
                  required 
                  defaultValue={editingClient?.type_client || ''}
                >
                  <option value="">Sélectionnez un type</option>
                  {settings?.types_clients?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  )) || (
                    <>
                      <option value="GMS">GMS</option>
                      <option value="boutique">Boutique</option>
                      <option value="restaurant">Restaurant</option>
                    </>
                  )}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Modifier' : 'Créer'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowClientForm(false);
                    setEditingClient(null);
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table des clients */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="table-hide-mobile"></th>
              <th>Entreprise</th>
              <th className="table-hide-mobile">Type</th>
              <th className="table-hide-mobile">Téléphone</th>
              <th className="table-hide-mobile">Email</th>
              <th className="table-hide-mobile">CA Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(filteredClients, currentPage, itemsPerPage).map(client => (
              <React.Fragment key={client.id}>
                <tr data-testid={`client-row-${client.id}`}>
                  <td className="table-hide-mobile">
                    <button
                      className={`mobile-details-btn ${expandedRows.has(client.id) ? 'expanded' : ''}`}
                      onClick={() => toggleRowExpansion(client.id)}
                      title="Voir les détails"
                    >
                      {expandedRows.has(client.id) ? '−' : '+'}
                    </button>
                  </td>
                  <td>
                    <span 
                      onClick={() => openClientDetail(client)}
                      className="client-name-clickable"
                      title="Cliquer pour voir l'historique"
                    >
                      {client.nom_entreprise}
                    </span>
                  </td>
                  <td className="table-hide-mobile">
                    <span className={`type-badge ${client.type_client}`}>
                      {getDisplayClientType(client.type_client)}
                    </span>
                  </td>
                  <td className="table-hide-mobile">{client.telephone}</td>
                  <td className="table-hide-mobile">{client.email}</td>
                  <td className="table-hide-mobile">
                    <strong>{formatCurrency(client.ca_total || getClientCA(client.id, settings?.devise))}</strong>
                  </td>
                  <td>
                    <ActionMenu
                      testId={`client-actions-${client.id}`}
                      items={[
                        {
                          label: "Voir l'historique",
                          icon: History,
                          onClick: () => openClientDetail(client),
                          testId: `menu-client-history-${client.id}`,
                        },
                        ...(hasWriteAccess() ? [
                          {
                            label: 'Modifier le client',
                            icon: Pencil,
                            onClick: () => {
                              setEditingClient(client);
                              setShowClientForm(true);
                            },
                            testId: `menu-client-edit-${client.id}`,
                          },
                          { type: 'separator' },
                          {
                            label: 'Supprimer définitivement',
                            icon: Trash2,
                            onClick: () => deleteClient(client.id),
                            variant: 'danger',
                            testId: `menu-client-delete-${client.id}`,
                          },
                        ] : []),
                      ]}
                    />
                  </td>
                </tr>
                {/* Ligne de détails pour mobile */}
                <tr className={`mobile-details-row ${expandedRows.has(client.id) ? 'expanded' : ''}`}>
                  <td colSpan="7">
                    <div className="mobile-details-content">
                      <div className="mobile-details-grid">
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Type</span>
                          <span className="mobile-detail-value">
                            <span className={`type-badge ${client.type_client}`}>
                              {getDisplayClientType(client.type_client)}
                            </span>
                          </span>
                        </div>
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Téléphone</span>
                          <span className="mobile-detail-value">{client.telephone}</span>
                        </div>
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Email</span>
                          <span className="mobile-detail-value">{client.email}</span>
                        </div>
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">Adresse</span>
                          <span className="mobile-detail-value">{client.adresse}</span>
                        </div>
                        <div className="mobile-detail-item">
                          <span className="mobile-detail-label">CA Total</span>
                          <span className="mobile-detail-value">
                            <strong>{formatCurrency(client.ca_total || getClientCA(client.id, settings?.devise))}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {filteredClients.length === 0 && (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '2rem', color: '#6c757d'}}>
                  {clientTypeFilter || clientCaFilter || searchTermClients ? 
                    'Aucun client trouvé avec ces filtres' : 
                    'Aucun client enregistré'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination 
        items={filteredClients}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );
};

export default ClientsList;
